/**
 * Mock recipes data based on actual S3 recipes for demo mode
 * These recipes mirror the structure in extracted-resources/s3-recipes/recipe-samples
 */

export interface MockRecipe {
  practice: string;
  workflow: string;
  displayName: string;
  description: string;
  hasUiSchema: boolean;
  schemaVersion: string;
  published: boolean;
  lastModified: string;
  completionPercentage: number;
  status: 'draft' | 'review' | 'published';
  internal?: boolean;
  unified_workflow?: {
    enabled: boolean;
    display_name: string;
    unified_id: string;
    steps: Array<{ practice: string; workflow: string; label: string }>;
  };
}

export interface MockRecipeDetail {
  practice: string;
  workflow: string;
  recipe: any;
  uiSchema: any;
  files: string[];
}

// Actual recipes from S3
export const MOCK_RECIPES: MockRecipe[] = [
  // Corporate Practice
  {
    practice: 'corporate',
    workflow: 'nda-analysis',
    displayName: 'NDA Analysis',
    description: 'Analyzes NDAs for affiliate binding and standstill provisions in M&A transactions',
    hasUiSchema: false,
    schemaVersion: '1.0',
    published: false,
    lastModified: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    completionPercentage: 85,
    status: 'draft'
  },
  {
    practice: 'corporate',
    workflow: 'power-purchase',
    displayName: 'Power Purchase Agreement',
    description: 'Extracts key features from power purchase agreements in PDF format',
    hasUiSchema: false,
    schemaVersion: '1.0',
    published: true,
    lastModified: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    completionPercentage: 100,
    status: 'published'
  },
  {
    practice: 'corporate',
    workflow: 'horizon-step-one',
    displayName: 'Horizon Analysis - Step 1',
    description: 'First pass categorization and sorting of input documents for corporate transactions',
    hasUiSchema: false,
    schemaVersion: '1.0',
    published: true,
    lastModified: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    completionPercentage: 100,
    status: 'published',
    internal: false,
    unified_workflow: {
      enabled: true,
      display_name: 'Horizon Analysis',
      unified_id: 'horizon',
      steps: [
        { practice: 'corporate', workflow: 'horizon-step-one', label: 'Document Sorting' },
        { practice: 'corporate', workflow: 'horizon-step-two', label: 'Deep Analysis' }
      ]
    }
  },
  {
    practice: 'corporate',
    workflow: 'horizon-step-two',
    displayName: 'Horizon Analysis - Step 2',
    description: 'Deep analysis phase for Horizon corporate transaction review',
    hasUiSchema: false,
    schemaVersion: '1.0',
    published: true,
    lastModified: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    completionPercentage: 100,
    status: 'published',
    internal: true
  },
  {
    practice: 'corporate',
    workflow: 'airplane-lease-step-one',
    displayName: 'Aircraft Lease Analysis - Step 1',
    description: 'Initial extraction and analysis of aircraft lease agreements',
    hasUiSchema: true,
    schemaVersion: '1.0',
    published: true,
    lastModified: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
    completionPercentage: 100,
    status: 'published',
    unified_workflow: {
      enabled: true,
      display_name: 'Aircraft Lease Analysis',
      unified_id: 'airplane-lease',
      steps: [
        { practice: 'corporate', workflow: 'airplane-lease-step-one', label: 'Initial Extraction' },
        { practice: 'corporate', workflow: 'airplane-lease-step-two', label: 'Detailed Review' }
      ]
    }
  },
  {
    practice: 'corporate',
    workflow: 'airplane-lease-step-two',
    displayName: 'Aircraft Lease Analysis - Step 2',
    description: 'Detailed review and human-in-the-loop verification for aircraft leases',
    hasUiSchema: true,
    schemaVersion: '1.0',
    published: true,
    lastModified: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
    completionPercentage: 100,
    status: 'published',
    internal: true
  },

  // Litigation Practice
  {
    practice: 'litigation',
    workflow: 'complaint-summary',
    displayName: 'Complaint Summary',
    description: 'Summarizes legal complaints with parties, claims, events, and witnesses',
    hasUiSchema: false,
    schemaVersion: '1.0',
    published: true,
    lastModified: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    completionPercentage: 100,
    status: 'published'
  },
  {
    practice: 'litigation',
    workflow: 'depo-digest',
    displayName: 'Deposition Digest',
    description: 'Creates digestible summaries of deposition transcripts organized by topic',
    hasUiSchema: false,
    schemaVersion: '1.0',
    published: true,
    lastModified: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    completionPercentage: 100,
    status: 'published'
  },
  {
    practice: 'litigation',
    workflow: 'pairwise-comparison',
    displayName: 'Pairwise Document Comparison',
    description: 'Compares documents pairwise to identify inconsistencies and contradictions',
    hasUiSchema: false,
    schemaVersion: '1.0',
    published: true,
    lastModified: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    completionPercentage: 100,
    status: 'published'
  },
  {
    practice: 'litigation',
    workflow: 'condense-complaint-by-witness',
    displayName: 'Complaint by Witness',
    description: 'Condenses complaint information organized by witness for trial preparation',
    hasUiSchema: false,
    schemaVersion: '1.0',
    published: true,
    lastModified: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
    completionPercentage: 100,
    status: 'published'
  },
  {
    practice: 'litigation',
    workflow: 'cross-doc-inconsistency',
    displayName: 'Cross-Document Inconsistency',
    description: 'Identifies inconsistencies across multiple related documents',
    hasUiSchema: false,
    schemaVersion: '1.0',
    published: false,
    lastModified: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    completionPercentage: 75,
    status: 'review'
  },

  // Patent Practice
  {
    practice: 'patent',
    workflow: 'section-101-analysis',
    displayName: 'Section 101 Analysis',
    description: 'Analyzes patent claims for Section 101 subject matter eligibility',
    hasUiSchema: false,
    schemaVersion: '1.0',
    published: true,
    lastModified: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    completionPercentage: 100,
    status: 'published'
  },
  {
    practice: 'patent',
    workflow: 'rfp',
    displayName: 'Request for Production',
    description: 'Generates and analyzes responses to requests for production in patent litigation',
    hasUiSchema: false,
    schemaVersion: '1.0',
    published: true,
    lastModified: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
    completionPercentage: 100,
    status: 'published'
  },

  // Trademark Practice
  {
    practice: 'trademark',
    workflow: 'ttab',
    displayName: 'TTAB Analysis',
    description: 'Trademark Trial and Appeal Board case analysis and preparation',
    hasUiSchema: false,
    schemaVersion: '1.0',
    published: true,
    lastModified: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    completionPercentage: 100,
    status: 'published'
  },

  // Real Estate Practice
  {
    practice: 'real-estate',
    workflow: 'lease-abstraction',
    displayName: 'Lease Abstraction',
    description: 'Extracts key terms and provisions from commercial leases into Excel summaries',
    hasUiSchema: true,
    schemaVersion: '1.0',
    published: true,
    lastModified: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
    completionPercentage: 100,
    status: 'published'
  },
  {
    practice: 'real-estate',
    workflow: 'ocean-bank-step-one',
    displayName: 'Ocean Bank Analysis - Step 1',
    description: 'Initial document processing for Ocean Bank real estate transactions',
    hasUiSchema: true,
    schemaVersion: '1.0',
    published: true,
    lastModified: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    completionPercentage: 100,
    status: 'published',
    unified_workflow: {
      enabled: true,
      display_name: 'Ocean Bank Analysis',
      unified_id: 'ocean-bank',
      steps: [
        { practice: 'real-estate', workflow: 'ocean-bank-step-one', label: 'Document Processing' },
        { practice: 'real-estate', workflow: 'ocean-bank-step-two', label: 'Review & Finalize' }
      ]
    }
  },
  {
    practice: 'real-estate',
    workflow: 'ocean-bank-step-two',
    displayName: 'Ocean Bank Analysis - Step 2',
    description: 'Review and finalization for Ocean Bank real estate analysis',
    hasUiSchema: true,
    schemaVersion: '1.0',
    published: true,
    lastModified: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    completionPercentage: 100,
    status: 'published',
    internal: true
  },
  {
    practice: 'real-estate',
    workflow: 'loan-commitment',
    displayName: 'Loan Commitment Analysis',
    description: 'Analyzes loan commitment letters and documentation',
    hasUiSchema: false,
    schemaVersion: '1.0',
    published: true,
    lastModified: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    completionPercentage: 100,
    status: 'published'
  },

  // Trial Prep Practice
  {
    practice: 'trial-prep',
    workflow: 'testimony',
    displayName: 'Testimony Analysis',
    description: 'Analyzes and organizes witness testimony for trial preparation',
    hasUiSchema: false,
    schemaVersion: '1.0',
    published: true,
    lastModified: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    completionPercentage: 100,
    status: 'published'
  }
];

// Actual recipe structures from S3
export const MOCK_RECIPE_DETAILS: Record<string, any> = {
  'corporate/nda-analysis': {
    metadata: {
      practice: 'corporate',
      workflow: 'nda-analysis',
      default_prompt_folder: 'corporate/nda-analysis',
      skip_textract: false,
      description: 'Analyzes NDAs for affiliate binding and standstill provisions in M&A transactions',
      published: false,
      supported_formats: ['pdf'],
      comment: 'NDA analysis focusing on affiliate binding and standstill provisions.'
    },
    orchestration: {
      type: 'parallel_docs',
      aggregate: 'merge_rows'
    },
    draft: [
      {
        prompt_template: 'extraction - affiliate binding and restrictive covenants',
        context_keys: ['input'],
        context: {
          target_entities: 'R2 Solutions LLC, Stingray IP Solutions LLC, Atlas Global Technologies LLC'
        }
      }
    ],
    structure: [
      {
        prompt_template: 'structure',
        context_keys: ['input']
      }
    ],
    postprocess: 'select_final_response'
  },

  'corporate/power-purchase': {
    metadata: {
      practice: 'corporate',
      workflow: 'power-purchase',
      default_prompt_folder: 'corporate/power-purchase',
      skip_textract: false,
      description: 'Extracts key features from power purchase agreements in PDF format',
      published: true,
      supported_formats: ['pdf']
    },
    orchestration: {
      type: 'parallel_docs',
      aggregate: 'merge_rows'
    },
    draft: [
      {
        prompt_template: 'extraction - ppa terms',
        context_keys: ['input']
      }
    ],
    structure: [
      {
        prompt_template: 'structure',
        context_keys: ['input']
      }
    ],
    postprocess: 'select_final_response'
  },

  'corporate/horizon-step-one': {
    metadata: {
      practice: 'corporate',
      workflow: 'horizon-step-one',
      default_prompt_folder: 'corporate/horizon-step-one',
      skip_textract: false,
      comment: 'First pass just for categorization and sorting of input documents'
    },
    orchestration: {
      type: 'map',
      map_key: null,
      aggregate: 'merge_rows'
    },
    draft: [
      { prompt_template: 'core/extraction/load file only', context_keys: ['input'] },
      { prompt_template: 'extraction - document metadata' },
      { prompt_template: 'reasoning - determine document type' },
      { prompt_template: 'reasoning - identify related documents' },
      { prompt_template: 'reasoning - assess document relevance' }
    ],
    structure: [
      {
        prompt_template: 'structure',
        context_keys: ['input']
      }
    ],
    postprocess: 'select_final_response'
  },

  'corporate/horizon-step-two': {
    metadata: {
      practice: 'corporate',
      workflow: 'horizon-step-two',
      default_prompt_folder: 'corporate/horizon-step-two',
      skip_textract: false,
      comment: 'Deep analysis of categorized documents'
    },
    orchestration: {
      type: 'parallel_docs',
      aggregate: 'merge_rows'
    },
    draft: [
      { prompt_template: 'extraction - detailed analysis', context_keys: ['input'] }
    ],
    structure: [
      { prompt_template: 'structure', context_keys: ['input'] }
    ],
    postprocess: 'select_final_response'
  },

  'corporate/airplane-lease-step-one': {
    metadata: {
      practice: 'corporate',
      workflow: 'airplane-lease-step-one',
      default_prompt_folder: 'corporate/airplane-lease-step-one',
      skip_textract: false,
      description: 'Initial extraction and analysis of aircraft lease agreements'
    },
    orchestration: {
      type: 'parallel_docs',
      aggregate: 'merge_rows'
    },
    draft: [
      { prompt_template: 'extraction - lease terms', context_keys: ['input'] },
      { prompt_template: 'extraction - aircraft details', context_keys: ['input'] }
    ],
    structure: [
      { prompt_template: 'structure', context_keys: ['input'] }
    ],
    postprocess: 'select_final_response'
  },

  'corporate/airplane-lease-step-two': {
    metadata: {
      practice: 'corporate',
      workflow: 'airplane-lease-step-two',
      default_prompt_folder: 'corporate/airplane-lease-step-two',
      skip_textract: false,
      description: 'Detailed review with human-in-the-loop verification'
    },
    orchestration: {
      type: 'sequential_docs'
    },
    draft: [
      { prompt_template: 'review - verify extraction', context_keys: ['input', 'previous_step_output'] }
    ],
    structure: [
      { prompt_template: 'structure - final', context_keys: ['input'] }
    ],
    postprocess: 'select_final_response',
    render: {
      template: 'aircraft-lease-report'
    }
  },

  'litigation/complaint-summary': {
    draft: [
      { prompt_template: 'core/extraction/parties', context_keys: ['input'] },
      { prompt_template: 'litigation/extraction/claims - no input file' },
      { prompt_template: 'core/extraction/events - no input file', context_keys: ['extraction_hint'] },
      { prompt_template: 'core/extraction/witnesses - no input file', context_keys: ['extraction_hint'] },
      { content: 'Thoroughly summarize the complaint, being sure to incorporate the parties, claims, events, and witnesses provided above.' }
    ],
    structure: [
      { prompt_template: 'litigation/complaint-summary/structure', context_keys: ['input'] }
    ],
    postprocess: 'select_final_response',
    render: {
      template: 'default'
    }
  },

  'litigation/depo-digest': {
    orchestration: {
      type: 'parallel_docs',
      aggregate: 'merge_rows'
    },
    draft: [
      { prompt_template: 'extraction - deposition topics', context_keys: ['input'] },
      { prompt_template: 'extraction - key testimony', context_keys: ['input'] }
    ],
    structure: [
      { prompt_template: 'structure - digest', context_keys: ['input'] }
    ],
    postprocess: 'select_final_response'
  },

  'litigation/pairwise-comparison': {
    orchestration: {
      type: 'pairwise',
      aggregate: 'merge_inconsistencies'
    },
    draft: [
      { prompt_template: 'comparison - identify differences', context_keys: ['doc_a', 'doc_b'] },
      { prompt_template: 'analysis - assess significance' }
    ],
    structure: [
      { prompt_template: 'structure - comparison report', context_keys: ['input'] }
    ],
    postprocess: 'select_final_response'
  },

  'litigation/condense-complaint-by-witness': {
    draft: [
      { prompt_template: 'extraction - witnesses', context_keys: ['input'] },
      { prompt_template: 'organization - by witness' }
    ],
    structure: [
      { prompt_template: 'structure - witness summary', context_keys: ['input'] }
    ],
    postprocess: 'select_final_response'
  },

  'litigation/cross-doc-inconsistency': {
    orchestration: {
      type: 'parallel_docs',
      aggregate: 'collect_inconsistencies'
    },
    draft: [
      { prompt_template: 'extraction - key facts', context_keys: ['input'] },
      { prompt_template: 'comparison - cross reference' }
    ],
    structure: [
      { prompt_template: 'structure - inconsistency report', context_keys: ['input'] }
    ],
    postprocess: 'select_final_response'
  },

  'patent/section-101-analysis': {
    metadata: {
      practice: 'patent',
      workflow: 'section-101-analysis',
      description: 'Analyzes patent claims for Section 101 subject matter eligibility'
    },
    draft: [
      { prompt_template: 'extraction - claim elements', context_keys: ['input'] },
      { prompt_template: 'analysis - alice test step one' },
      { prompt_template: 'analysis - alice test step two' }
    ],
    structure: [
      { prompt_template: 'structure - 101 analysis', context_keys: ['input'] }
    ],
    postprocess: 'select_final_response'
  },

  'patent/rfp': {
    metadata: {
      practice: 'patent',
      workflow: 'rfp',
      description: 'Generates and analyzes responses to requests for production'
    },
    orchestration: {
      type: 'parallel_docs'
    },
    draft: [
      { prompt_template: 'extraction - rfp requirements', context_keys: ['input'] },
      { prompt_template: 'generation - response draft' }
    ],
    structure: [
      { prompt_template: 'structure - rfp response', context_keys: ['input'] }
    ],
    postprocess: 'select_final_response'
  },

  'trademark/ttab': {
    metadata: {
      practice: 'trademark',
      workflow: 'ttab',
      description: 'Trademark Trial and Appeal Board case analysis'
    },
    draft: [
      { prompt_template: 'extraction - ttab case details', context_keys: ['input'] },
      { prompt_template: 'analysis - likelihood of confusion' },
      { prompt_template: 'analysis - prior precedent' }
    ],
    structure: [
      { prompt_template: 'structure - ttab analysis', context_keys: ['input'] }
    ],
    postprocess: 'select_final_response'
  },

  'real-estate/lease-abstraction': {
    metadata: {
      practice: 'real-estate',
      workflow: 'lease-abstraction',
      default_prompt_folder: 'real-estate/lease-abstraction',
      skip_textract: false,
      description: 'Extracts key features from commercial leases in PDF format'
    },
    orchestration: {
      type: 'parallel_docs',
      aggregate: 'merge_rows'
    },
    draft: [
      { prompt_template: 'extraction - lease terms', context_keys: ['input'] },
      { prompt_template: 'extraction - rent provisions', context_keys: ['input'] },
      { prompt_template: 'extraction - termination clauses', context_keys: ['input'] }
    ],
    structure: [
      { prompt_template: 'structure - lease abstract', context_keys: ['input'] }
    ],
    postprocess: 'select_final_response',
    render: {
      template: 'excel-lease-abstract'
    }
  },

  'real-estate/ocean-bank-step-one': {
    metadata: {
      practice: 'real-estate',
      workflow: 'ocean-bank-step-one',
      description: 'Initial document processing for Ocean Bank transactions'
    },
    orchestration: {
      type: 'parallel_docs',
      aggregate: 'merge_rows'
    },
    draft: [
      { prompt_template: 'extraction - document classification', context_keys: ['input'] },
      { prompt_template: 'extraction - key terms', context_keys: ['input'] }
    ],
    structure: [
      { prompt_template: 'structure', context_keys: ['input'] }
    ],
    postprocess: 'select_final_response'
  },

  'real-estate/ocean-bank-step-two': {
    metadata: {
      practice: 'real-estate',
      workflow: 'ocean-bank-step-two',
      description: 'Review and finalization for Ocean Bank analysis'
    },
    orchestration: {
      type: 'sequential_docs'
    },
    draft: [
      { prompt_template: 'review - verify data', context_keys: ['input', 'previous_output'] }
    ],
    structure: [
      { prompt_template: 'structure - final report', context_keys: ['input'] }
    ],
    postprocess: 'select_final_response',
    render: {
      template: 'ocean-bank-report'
    }
  },

  'real-estate/loan-commitment': {
    metadata: {
      practice: 'real-estate',
      workflow: 'loan-commitment',
      description: 'Analyzes loan commitment letters and documentation'
    },
    draft: [
      { prompt_template: 'extraction - commitment terms', context_keys: ['input'] },
      { prompt_template: 'extraction - conditions precedent', context_keys: ['input'] }
    ],
    structure: [
      { prompt_template: 'structure - commitment summary', context_keys: ['input'] }
    ],
    postprocess: 'select_final_response'
  },

  'trial-prep/testimony': {
    metadata: {
      practice: 'trial-prep',
      workflow: 'testimony',
      description: 'Analyzes and organizes witness testimony for trial'
    },
    orchestration: {
      type: 'parallel_docs',
      aggregate: 'merge_by_witness'
    },
    draft: [
      { prompt_template: 'extraction - testimony highlights', context_keys: ['input'] },
      { prompt_template: 'analysis - credibility factors' },
      { prompt_template: 'organization - by topic' }
    ],
    structure: [
      { prompt_template: 'structure - testimony summary', context_keys: ['input'] }
    ],
    postprocess: 'select_final_response'
  }
};

// UI Schema samples for workflows that have them
export const MOCK_UI_SCHEMAS: Record<string, any> = {
  'real-estate/lease-abstraction': {
    metadata: {
      practice: 'real-estate',
      workflow: 'lease-abstraction',
      version: '1.0.0',
      display_name: 'Lease Abstraction',
      description: 'Extracts key features from commercial leases in PDF format, preparing an Excel summary report.'
    },
    workflow_type: 'single-step',
    output_handling: {
      primary_output_type: 'xlsx',
      show_download_for_types: ['xlsx', 'pdf', 'docx'],
      hide_output_types: ['json'],
      download_filename_template: '{job_number}.{extension}'
    },
    completion_actions: {
      show_review_button: false,
      completion_message: 'Lease abstraction complete. Your Excel summary is ready for download.'
    },
    help_text: {
      overview: '# Lease Abstraction\n\n## Overview\nExtract key terms and provisions from commercial lease agreements.\n\n## Instructions\n1. **Upload Lease Documents**: PDF or DOCX lease agreements\n2. **Automated Extraction**: AI identifies critical lease terms\n3. **Excel Output**: Generates structured lease abstract in Excel format',
      tips: [
        'Ensure lease documents are complete and legible',
        'Include all amendments and addenda',
        'Review extracted data for accuracy'
      ]
    },
    review_form: {
      enabled: false
    }
  },
  'corporate/airplane-lease-step-one': {
    metadata: {
      practice: 'corporate',
      workflow: 'airplane-lease-step-one',
      version: '1.0.0',
      display_name: 'Aircraft Lease - Initial Extraction',
      description: 'Initial extraction and analysis of aircraft lease agreements'
    },
    workflow_type: 'multi-step',
    internal: false,
    unified_workflow: {
      enabled: true,
      display_name: 'Aircraft Lease Analysis',
      unified_id: 'airplane-lease',
      steps: [
        { practice: 'corporate', workflow: 'airplane-lease-step-one', label: 'Initial Extraction' },
        { practice: 'corporate', workflow: 'airplane-lease-step-two', label: 'Review & Finalize' }
      ]
    },
    step_controls: {
      allow_intermediate_download: true,
      downloadable_types: ['json', 'xlsx'],
      allow_intermediate_edit: true,
      allow_manual_trigger: true,
      download_button_text: 'Download Extraction Data',
      edit_button_text: 'Review & Edit',
      trigger_button_text: 'Continue to Final Review'
    },
    help_text: {
      overview: '# Aircraft Lease Analysis\n\nThis multi-step workflow extracts and analyzes aircraft lease agreements.',
      tips: ['Upload complete lease documents', 'Review extracted terms carefully before proceeding']
    }
  },
  'corporate/airplane-lease-step-two': {
    metadata: {
      practice: 'corporate',
      workflow: 'airplane-lease-step-two',
      version: '1.0.0',
      display_name: 'Aircraft Lease - Final Review',
      description: 'Human-in-the-loop review and finalization'
    },
    workflow_type: 'multi-step',
    internal: true,
    review_form: {
      enabled: true,
      form_schema: {
        type: 'object',
        properties: {
          verified: { type: 'boolean', title: 'Data Verified' },
          notes: { type: 'string', title: 'Review Notes' }
        }
      }
    }
  },
  'real-estate/ocean-bank-step-one': {
    metadata: {
      practice: 'real-estate',
      workflow: 'ocean-bank-step-one',
      version: '1.0.0',
      display_name: 'Ocean Bank - Document Processing',
      description: 'Initial document processing for Ocean Bank transactions'
    },
    workflow_type: 'multi-step',
    unified_workflow: {
      enabled: true,
      display_name: 'Ocean Bank Analysis',
      unified_id: 'ocean-bank',
      steps: [
        { practice: 'real-estate', workflow: 'ocean-bank-step-one', label: 'Document Processing' },
        { practice: 'real-estate', workflow: 'ocean-bank-step-two', label: 'Review & Finalize' }
      ]
    },
    step_controls: {
      allow_intermediate_download: true,
      allow_intermediate_edit: true,
      allow_manual_trigger: true
    }
  },
  'real-estate/ocean-bank-step-two': {
    metadata: {
      practice: 'real-estate',
      workflow: 'ocean-bank-step-two',
      version: '1.0.0',
      display_name: 'Ocean Bank - Review & Finalize',
      description: 'Review and finalization for Ocean Bank analysis'
    },
    workflow_type: 'multi-step',
    internal: true,
    review_form: {
      enabled: true
    }
  }
};

// Helper to generate display name from workflow id
export function getDisplayName(workflow: string): string {
  return workflow
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// Get all unique practices
export function getPractices(): string[] {
  return [...new Set(MOCK_RECIPES.map(r => r.practice))];
}

// Get recipes for a specific practice
export function getRecipesForPractice(practice: string): MockRecipe[] {
  return MOCK_RECIPES.filter(r => r.practice === practice);
}

// Get recipe detail
export function getRecipeDetail(practice: string, workflow: string): MockRecipeDetail {
  const key = `${practice}/${workflow}`;
  const recipe = MOCK_RECIPE_DETAILS[key] || {
    metadata: { practice, workflow },
    draft: [{ prompt_template: 'extraction', context_keys: ['input'] }],
    structure: [{ prompt_template: 'structure', context_keys: ['input'] }],
    postprocess: 'select_final_response'
  };
  
  const uiSchema = MOCK_UI_SCHEMAS[key] || {
    metadata: { practice, workflow, display_name: getDisplayName(workflow) },
    workflow_type: 'single-step'
  };

  return {
    practice,
    workflow,
    recipe,
    uiSchema,
    files: ['recipe.json', 'ui-schema.json']
  };
}
